import { Chip, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitiesSearchAutocomplete, {
  BaseEntitiesSearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitiesSearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { AutocompleteOptionType } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { UserDto, UserGetPaginatedDto } from "@/core/api/generated/v1";

import { userToOption } from "./UserAutocomplete";

export interface UsersAutocompleteProps extends BaseEntitiesSearchAutocompleteInheritableProps<UserDto> {
  searchFilters?: {
    userId?: string;
    departmentId?: string;
  } & Partial<Omit<UserGetPaginatedDto, "search" | "includeIds">>;
}

export default function UsersAutocomplete({
  isPreload = true,
  searchFilters,
  ...otherProps
}: UsersAutocompleteProps) {
  const profile = useUserProfile();

  return (
    <BaseEntitiesSearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={userToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.usersApi.apiV1UsersGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          userGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          userSearchPaginatedDto: {
            ...params.userGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='User'
      placeholder='Search for user...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='user' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  variant={
                    option.optionType === AutocompleteOptionType.DynamicCreateNew
                      ? "subtitle1"
                      : "body1"
                  }
                  component='div'
                >
                  {option.title}
                  {option.data?.id === profile?.id && (
                    <>
                      {" "}
                      <Chip variant='outlined' size='small' color='default' label='You' />
                    </>
                  )}
                </Typography>
              }
              secondary={
                option.data && (
                  <Typography component='div' variant='body2'>
                    {option.data?.email} / {option.data?.phoneNumber}
                  </Typography>
                )
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
